import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Package, StickyNote, Trash2 } from 'lucide-react';
import type { CartItem } from '@/services/types';
import { IconTile, Stepper } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';

export interface CartLineProps {
    item: CartItem;
    onQty: (next: number) => void;
    onNotes: (notes: string) => void;
    onRemove: () => void;
    showThumb?: boolean;
    /** 48 (phone) or 60 (lg cart page) */
    thumbSize?: 48 | 60;
    stepperSize?: 'sm' | 'md' | 'lg';
    className?: string;
}

/** O2 cart line: thumb · name · unit price · note · line total · stepper. */
export const CartLine: React.FC<CartLineProps> = ({ item, onQty, onNotes, onRemove, showThumb = true, thumbSize = 48, stepperSize = 'sm', className }) => {
    const { t } = useTranslation();
    const [editing, setEditing] = useState(false);
    const showNote = editing || !!item.notes;

    return (
        <div className={clsx('flex gap-3 py-3.5', className)}>
            {showThumb && (
                <IconTile size={thumbSize} tone="sunken">
                    <Package />
                </IconTile>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <span className="text-[15px] font-extrabold leading-tight">{item.name || `#${item.compositeId}`}</span>
                <span className="text-xs text-ink-muted tnum">{item.variantName ? `${item.variantName} · ` : ''}{t('cart.perItem', { amount: formatCurrency(item.unitPrice || 0) })}</span>
                {showNote ? (
                    <span className="flex items-center gap-1 text-xs font-bold text-ink-secondary">
                        <StickyNote className="h-[13px] w-[13px] shrink-0" />
                        <input
                            autoFocus={editing}
                            value={item.notes || ''}
                            placeholder={t('cart.itemNotePlaceholder')}
                            onChange={(e) => onNotes(e.target.value)}
                            onBlur={() => setEditing(false)}
                            className="min-w-0 flex-1 bg-transparent outline-none placeholder:font-normal placeholder:text-ink-muted"
                        />
                    </span>
                ) : (
                    <button type="button" onClick={() => setEditing(true)} className="self-start text-xs font-extrabold text-ink-accent">
                        + {t('cart.addItemNote')}
                    </button>
                )}
            </div>
            <div className="flex flex-col items-end gap-2">
                <span className="text-[15px] font-black tnum">{formatCurrency((item.unitPrice || 0) * item.quantity)}</span>
                <div className="flex items-center gap-1.5">
                    <Stepper size={stepperSize} value={item.quantity} onChange={onQty} />
                    <button type="button" onClick={onRemove} aria-label={t('common.delete')} className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-full text-ink-muted hover:bg-status-danger-soft hover:text-status-danger">
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};
export default CartLine;
